import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { appListenerMiddleware } from 'reduxTyped'
import { createSelector } from 'reselect'
import { l } from '@manychat/manyui'

import { MAX_KNOWLEDGE_ENTRIES } from 'apps/aiAutomation/lib/knowledge'
import {
  AiAlwaysOnAutomationsActions,
  AiAlwaysOnAutomationsSelectors,
} from 'apps/aiAutomation/store/core'
import { AiAutomationModel } from 'apps/aiAutomation/store/core/lib/model'
import { AiAutomationKnowledgeState, LoaderStatus } from 'apps/aiAutomation/store/knowledge/types'
import { alert } from 'common/core'
import { getAccountFlags } from 'common/core/selectors/appSelectors'
import { KnowledgeEntry } from 'shared/api/requests/aiAutomation/schemas'
import { AiBusinessKnowledgeApi } from 'shared/api/requests/aiBusinessKnowledge'
import {
  KnowledgeEntry as KnowledgeEntryNew,
  KnowledgeEntryType,
  TextEntry,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

const NAMESPACE = 'aiAutomationKnowledge'

export const AiAutomationKnowledgeInitialState: AiAutomationKnowledgeState = {
  entries: [],
  selectedEntryIndex: null,
  loaderStatus: LoaderStatus.NOT_LOADED,
}

const { reducer, actions } = createSlice({
  initialState: AiAutomationKnowledgeInitialState,
  name: NAMESPACE,
  reducers: {
    selectedEntryIndexUpdated: (state, action: PayloadAction<number | string | null>) => {
      state.selectedEntryIndex = action.payload
    },
    entriesUpdated: (state, action: PayloadAction<KnowledgeEntry[] | undefined>) => {
      state.entries = action.payload || []
      state.loaderStatus = LoaderStatus.LOADED
    },
    entryAdded: (state, action: PayloadAction<KnowledgeEntryNew>) => {
      ;(state.entries as KnowledgeEntryNew[]).push(action.payload)
    },
    entryDeleted: (state, action: PayloadAction<string>) => {
      state.entries = (state.entries as KnowledgeEntryNew[]).filter(
        (entry) => entry.id !== action.payload,
      )
    },
    entryUpdated: (state, action: PayloadAction<KnowledgeEntryNew>) => {
      const entry = (state.entries as KnowledgeEntryNew[]).find(
        (entry) => entry.id === action.payload.id,
      )
      if (entry) {
        Object.assign(entry, action.payload)
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(retrieveEntriesFx.fulfilled, (state, action) => {
      state.entries = action.payload
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(AiAlwaysOnAutomationsActions.updateConfigFx.rejected, (state) => {
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addMatcher(
      isAnyOf(
        editEntryFx.pending.match,
        addEntryFx.pending.match,
        removeEntryFx.pending.match,
        AiAlwaysOnAutomationsActions.getConfigFx.pending.match,
      ),
      (state) => {
        state.loaderStatus = LoaderStatus.LOADING
      },
    )

    builder.addMatcher(
      isAnyOf(
        editEntryFx.fulfilled.match,
        addEntryFx.fulfilled.match,
        removeEntryFx.fulfilled.match,
        editEntryFx.rejected.match,
        addEntryFx.rejected.match,
        removeEntryFx.rejected.match,
      ),
      (state) => {
        state.loaderStatus = LoaderStatus.LOADED
      },
    )
  },
})

const retrieveEntriesFx = createAsyncAction(`${NAMESPACE}/retrieveEntriesFx`, async () => {
  const { data } = await AiBusinessKnowledgeApi.retrieve()

  return data.data
})

const addEntryFx = createAsyncAction(
  `${NAMESPACE}/addEntryFx`,
  async (data: TextEntry | KnowledgeEntry, { dispatch, getState }) => {
    const isNewApiEnabled = getNewApiEnabled(getState())
    const entries = AiAutomationKnowledgeSelectors.getKnowledge(getState())
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    if (entries.length >= MAX_KNOWLEDGE_ENTRIES) {
      alert(
        l.translate('You can’t add more than {count} entries<l10n context="Knowledge entries"/>', {
          count: MAX_KNOWLEDGE_ENTRIES,
        }),
        'danger',
      )
      return
    }

    if (isNewApiEnabled) {
      await AiBusinessKnowledgeApi.create({
        body: {
          data: data as KnowledgeEntryNew,
        },
      })
    } else {
      await dispatch(
        AiAlwaysOnAutomationsActions.updateConfigFx({
          business_context: [data as KnowledgeEntry, ...(entries as KnowledgeEntry[])],
        }),
      )
    }

    if (isAutomationPublished) {
      alert(l.translate('Info saved! AI will now use this to answer questions.'), 'success', {
        icon: 'CheckCircle',
      })
    }
  },
)

const editEntryFx = createAsyncAction(
  `${NAMESPACE}/editEntryFx`,
  async (data: TextEntry | KnowledgeEntry, { dispatch, getState }) => {
    const isNewApiEnabled = getNewApiEnabled(getState())
    const entries = AiAutomationKnowledgeSelectors.getKnowledge(getState())
    const index = AiAutomationKnowledgeSelectors.getSelectedEntryIndex(getState())

    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    if (isNewApiEnabled) {
      await AiBusinessKnowledgeApi.updateData({
        body: {
          data: data as KnowledgeEntryNew,
        },
      })
    } else {
      await dispatch(
        AiAlwaysOnAutomationsActions.updateConfigFx({
          business_context: AiAutomationModel.updateKnowledgeEntry(
            entries as KnowledgeEntry[],
            index as number,
            data as KnowledgeEntry,
          ),
        }),
      )
    }

    if (isAutomationPublished) {
      alert(
        l.translate(
          'Knowledge updated. AI is now using the latest version to answer customer questions.',
        ),
        'success',
        {
          icon: 'CheckCircle',
        },
      )
    }
  },
  {
    condition: (_, { getState }) =>
      Boolean(AiAutomationKnowledgeSelectors.getSelectedEntry(getState())),
  },
)

const removeEntryFx = createAsyncAction(
  `${NAMESPACE}/removeEntryFx`,
  async (indexOrId: number | string, { dispatch, getState }) => {
    const isNewApiEnabled = getNewApiEnabled(getState())

    const entries = AiAutomationKnowledgeSelectors.getKnowledge(getState())
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    if (isNewApiEnabled) {
      await AiBusinessKnowledgeApi.delete({
        body: {
          id: indexOrId as string,
        },
      })
      if (entries.length === 1) {
        await dispatch(
          AiAlwaysOnAutomationsActions.updateConfigFx({
            scenarios: {
              faq: {
                published: false,
              },
            },
          }),
        )
      }
    } else {
      await dispatch(
        AiAlwaysOnAutomationsActions.updateConfigFx({
          business_context: AiAutomationModel.removeKnowledgeEntry(
            entries as KnowledgeEntry[],
            indexOrId as number,
          ),
        }),
      )
    }

    if (isAutomationPublished) {
      alert(
        entries.length === 1
          ? l.translate('All the info was deleted! AI will no longer answer questions.')
          : l.translate('Info deleted! AI won’t answer questions related to that anymore.'),
        'success',
        {
          icon: 'CheckCircle',
        },
      )
    }
  },
)

export const AiAutomationKnowledgeActions = {
  selectedEntryIndexUpdated: actions.selectedEntryIndexUpdated,

  retrieveEntriesFx,
  addEntryFx,
  editEntryFx,
  removeEntryFx,
}

export const AiAutomationKnowledgeReducer = reducer

appListenerMiddleware.startListening({
  predicate: (action) =>
    [
      AiAlwaysOnAutomationsActions.getConfigFx.fulfilled.type,
      AiAlwaysOnAutomationsActions.updateConfigFx.fulfilled.type,
      AiAlwaysOnAutomationsActions.configUpdated.type,
    ].includes(action.type),
  effect: (action, { getState, dispatch }) => {
    const state = getState()
    if (!getNewApiEnabled(state)) {
      dispatch(actions.entriesUpdated(action.payload.business_context))
    }
  },
})

anotherTabNotificationsListener.on(
  'ai_business_knowledge_entry_created',
  (payload: { data: KnowledgeEntryNew }, dispatch) => {
    dispatch(actions.entryAdded(payload.data))
  },
)

anotherTabNotificationsListener.on(
  'ai_business_knowledge_entry_updated',
  (payload: { data: KnowledgeEntryNew }, dispatch) => {
    dispatch(actions.entryUpdated(payload.data))
  },
)

anotherTabNotificationsListener.on(
  'ai_business_knowledge_entry_deleted',
  (payload: { data: { id: string } }, dispatch) => {
    dispatch(actions.entryDeleted(payload.data.id))
  },
)

const getState = (state: RootState) => state.aiAutomation.knowledge

const getKnowledge = (state: RootState) => getState(state).entries

const getNewApiEnabled = createSelector(getAccountFlags, (accountFlags) => {
  return accountFlags[AccountFlag.ENABLE_AI_AUTOMATION_KNOWLEDGE_NEW_API]
})

const getUsage = createSelector([getKnowledge, getNewApiEnabled], (knowledge, isNewApiEnabled) => {
  if (isNewApiEnabled) {
    return (knowledge as KnowledgeEntryNew[])
      .filter(({ type }) => type === KnowledgeEntryType.TEXT)
      .reduce((size, knowledgeEntry) => size + (knowledgeEntry as TextEntry).data.text.length, 0)
  }
  return (knowledge as KnowledgeEntry[]).reduce(
    (size, knowledgeEntry) => size + knowledgeEntry.content.length,
    0,
  )
})

// this method should be named like getSelectedId
const getSelectedEntryIndex = (state: RootState) => getState(state).selectedEntryIndex

const getSelectedEntry = createSelector(
  [getKnowledge, getNewApiEnabled, getSelectedEntryIndex],
  (knowledge, isNewApiEnabled, selectedIndex) => {
    if (selectedIndex === null) {
      return null
    }

    if (isNewApiEnabled) {
      return (knowledge as KnowledgeEntryNew[]).find(({ id }) => id === selectedIndex) ?? null
    }

    return knowledge[selectedIndex as number]
  },
)

export const AiAutomationKnowledgeSelectors = {
  getLoaderStatus: (state: RootState) => getState(state).loaderStatus,
  getKnowledge,
  getSelectedEntryIndex,
  getSelectedEntry,
  getUsage,
}
